import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { IconImage } from '@hivebrite/design-system/Icon';
import { Loader } from '@hivebrite/design-system/Loader';
import { useMeasure } from '@hivebrite/react-hooks';

import type { ImageDimensions } from 'hb-builder/components/BuilderComponents/page/single/PageImage/types';
import Drop from 'hb-builder/components/common/Drop';
import ImageSetter from 'hb-builder/components/common/ImageSetter';
import EmptyState from 'hb-builder/components/common/state/EmptyState';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper, WrapperBuilder } from 'hb-builder/components/common/Wrapper';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useBuilderContext,
  useComponentContext,
  useBuilderConfigContext,
  useDeviceModeContext,
  useModalContext,
  useRendererContext,
  useSectionContext,
} from 'hb-builder/hooks';
import { getJustifyContent, hasLink } from 'hb-builder/utils/attributes';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { PageImageItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/pageImageItemAttributess';
import mergeRefs from 'hb-react/shared/utils/mergeRefs';

import ResizableImageContainer from './ResizableImageContainer';
import ResizedImageUi from './ResizedImageUi';

const componentType = 'single';
const emptyStateHeight = '200px';

const PageImage = () => <div />; // never used

const RenderBuilder = () => {
  const { resizedItemId, updateAttributes } = useBuilderContext();
  const { isMobileMode } = useDeviceModeContext();
  const { item } = useComponentContext();
  const {
    features: {
      image: { positionsHandler },
    },
  } = useBuilderConfigContext();

  const attributes = item?.attributes;
  const [ref, { width: wrapperWidth }] = useMeasure();
  const { setModalOpen } = useModalContext();
  const { nbFinalChildren, sectionHeight } = useSectionContext();

  const [mobileModeDimensions, setMobileModeDimensions] = useState<{
    width?: number;
    height?: number;
    constructionColumnWidth?: number;
  }>({
    width: attributes?.width,
    height: attributes?.height,
    constructionColumnWidth: attributes?.constructionColumnWidth,
  });

  const { _imageData, imageSrc: imageUrl } = item?.attributes || {};
  const imageSrc = _imageData || imageUrl;
  const isEmpty = !imageSrc;
  const isNarrow = wrapperWidth < 200;

  const handleEmptyDoubleClick = () =>
    !isMobileMode &&
    setModalOpen((prevIsModalOpen) => ({
      ...prevIsModalOpen,
      [item.id]: true,
    }));

  const handleResize = ({ value, wrapperWidth }) =>
    updateAttributes({
      id: item.id,
      values: [
        { attribute: 'imageFullWidth', value: value.imageFullWidth },
        { attribute: 'horizontalAlign', value: value.horizontalAlign },
        { attribute: 'verticalAlign', value: value.verticalAlign },
        { attribute: 'left', value: !value.horizontalAlign && value.x },
        { attribute: 'top', value: !value.verticalAlign && value.y },
        {
          // needed to calculate ratio dimension
          attribute: 'constructionColumnWidth',
          value: wrapperWidth,
        },
        {
          attribute: 'height',
          value: value.height,
        },
        {
          attribute: 'width',
          value: value.width,
        },
      ],
    });

  const getInitialDimensions = (
    value: ImageDimensions,
    wrapperWidth: number,
  ) => {
    // When switching to mobile mode, we don't want to update the JSON document as it pushes a new entry in the history,
    // so we added an internal state to store the image dimensions for mobile mode and we're passing the values to the ResizableImageContainer
    // (overriding the values from the JSON document)
    if (!isMobileMode) {
      updateAttributes({
        id: item.id,
        skipHistoryStackPush: true,
        values: [
          {
            attribute: 'height',
            value: value.height,
          },
          {
            attribute: 'width',
            value: value.width,
          },
          {
            attribute: 'constructionColumnWidth',
            value: wrapperWidth,
          },
        ],
      });
    } else {
      setMobileModeDimensions({
        ...value,
        constructionColumnWidth: wrapperWidth,
      });
    }
  };

  const renderComponent = (isOver: boolean) =>
    isEmpty ? (
      <EmptyState
        height={emptyStateHeight}
        isOver={isOver}
        nbFinalChildren={nbFinalChildren}
        onDoubleClick={handleEmptyDoubleClick}
        p={isNarrow ? 0 : 4}
        sectionHeight={sectionHeight}
      >
        <Box
          alignItems="center"
          color="primary"
          flexDirection="column"
          textAlign="center"
        >
          <IconImage size={50} />
          {!isNarrow && (
            <Box color="black">
              {s__(
                'Builder|Double click on the component to add an image or add it in the floating panel',
              )}
            </Box>
          )}
        </Box>
      </EmptyState>
    ) : (
      <>
        <ResizableImageContainer
          attributes={{
            ...attributes,
            ...((isMobileMode && mobileModeDimensions) || {}),
            imageSrc,
          }}
          getInitialDimensions={getInitialDimensions}
          handleResize={handleResize}
          isResizing={resizedItemId !== item.id}
          positionsHandler={positionsHandler}
        />
        {attributes?.isLoadingSrc && attributes?._imageData ? (
          <ImgLoaderWrapper dataTestId="image-loader" $absolute>
            <Loader size={40} />
          </ImgLoaderWrapper>
        ) : null}
      </>
    );

  useEffect(() => {
    // If the image dimensions are updated in the JSON document (ie. by resizing the image),
    // we need to reset the internal state for mobile mode and let the new values be passed to the ResizableImageContainer
    // otherwise the new height or width values won't be taken into account when computing the new image dimensions
    setMobileModeDimensions({});
  }, [attributes?.height, attributes?.width]);

  return (
    <>
      <Drop accept={['single']}>
        {({ isOver, ref: dropRef }) =>
          !attributes?._imageData && attributes?.isLoadingSrc ? (
            <ImgLoaderWrapper dataTestId="image-loader">
              <Loader size={40} />
            </ImgLoaderWrapper>
          ) : (
            // @ts-expect-error untyped
            <WrapperBuilder
              attributes={attributes}
              dataTestId="builder-component-image"
              isEmpty={isEmpty}
              ref={mergeRefs([ref, dropRef])}
              style={{
                // Safari's default setting for the align-items is stretch
                alignItems: 'flex-start',
                justifyContent: getJustifyContent(attributes?.horizontalAlign),
                ...(isEmpty && { height: '100%' }),
              }}
            >
              {({ isDragging }) =>
                isDragging ? null : renderComponent(isOver)
              }
            </WrapperBuilder>
          )
        }
      </Drop>
      <ImageSetter attributeName="imageSrc" item={item} />
    </>
  );
};

const Render = ({
  item,
}: RenderProps<DocumentItem<PageImageItemAttributes>>) => {
  const { isMobilePreview } = useRendererContext();

  if (!item || !item.attributes) {
    return null;
  }

  const { attributes } = item;

  return (
    // @ts-expect-error untyped
    <Wrapper
      attributes={attributes}
      componentType={componentType}
      dataTestId="builder-component-image"
      isMobilePreview={isMobilePreview}
      style={{
        // Safari's default setting for the align-items is stretch
        alignItems: 'flex-start',
        justifyContent: getJustifyContent(attributes?.horizontalAlign),
      }}
    >
      <ResizedImageUi
        attributes={attributes}
        hasLink={hasLink({ link: attributes?.imageLink })}
      />
    </Wrapper>
  );
};

PageImage.componentName = 'PageImage';
PageImage.componentType = componentType;

PageImage.componentLabel = __('Image');
PageImage.componentCategory = 'single';
PageImage.componentIcon = () => IconImage;

PageImage.canBeDoubleClicked = true;
PageImage.canBeDragged = true;
PageImage.canBeListed = true;
PageImage.canBeSelected = true;

PageImage.item = () => ({
  attributes: {
    horizontalAlign: 'center',
  },
  category: PageImage.componentCategory,
  name: PageImage.componentName,
  type: PageImage.componentName,
});

PageImage.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

PageImage.SettingsTabs = defaultTabs;

PageImage.RenderBuilder = RenderBuilder;
PageImage.Render = Render;

export default PageImage;

interface ImgLoaderWrapperProps {
  $absolute?: boolean;
}

const ImgLoaderWrapper = styled(Box).attrs<ImgLoaderWrapperProps>(
  ({ $absolute }) => ({
    alignItems: 'center',
    color: 'fog',
    height: $absolute ? '100%' : emptyStateHeight,
    justifyContent: 'center',
    p: 2,
    width: '100%',
  }),
)<ImgLoaderWrapperProps>`
  ${({ $absolute }) =>
    $absolute
      ? css`
          position: absolute;
          z-index: 301; // The image AbsoluteWrapper can have a 300 z-index value
        `
      : ''}
`;
